import React, { useState } from "react";
import { T } from "../theme/tokens.js";
import { Modal, Btn, Notice, Labelled } from "./ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { layerUrlOf, whereIn } from "../lib/arcgis.js";

const input = {
  width: "100%", padding: "8px 10px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 14, background: "#fff", color: T.ink,
};

/* Map layers this contract reads rather than copies.
 *
 * The address goes in once, on the contract, so everybody assigned to the
 * contract gets the same maps. A filter is optional and is written in ArcGIS's
 * own query language: the schools layer carries every school in the territory
 * and marks the twenty eight in the off-site program with Included = 'Y'.
 */
function MapSources({ sources, onChange }) {
  const set = (i, patch) => onChange(sources.map((s, n) => (n === i ? { ...s, ...patch } : s)));

  const add = () => onChange([...sources, {
    id: `src-${Date.now().toString(36)}`, title: "", url: "", where: "", description: "",
  }]);

  /* People paste whatever their browser is showing, which is often a whole
     query with the filter already in it. Taking the filter out of it saves
     them typing it again, and saves the double filter that would otherwise
     silently return nothing. */
  const paste = (i, raw) => {
    const filter = whereIn(raw);
    set(i, { url: raw, ...(filter && !sources[i].where ? { where: filter } : {}) });
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {sources.map((src, i) => {
        const layer = layerUrlOf(src.url);
        return (
          <div key={src.id ?? i} style={{ border: `1px solid ${T.line}`, borderRadius: 4,
                                          padding: 12, display: "grid", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
              <Labelled label="What to call it">
                <input style={input} value={src.title ?? ""} placeholder="Schools and off-site findings"
                  onChange={(e) => set(i, { title: e.target.value })} />
              </Labelled>
              <Labelled label="Only the features where" hint="Optional. Written the way ArcGIS writes a filter. Blank means all of them.">
                <input style={input} value={src.where ?? ""} placeholder="Included = 'Y'"
                  onChange={(e) => set(i, { where: e.target.value })} />
              </Labelled>
            </div>
            <Labelled label="Layer address"
              hint="Ends in FeatureServer, or FeatureServer/0. Paste the whole query if that is what you have.">
              <input style={input} value={src.url ?? ""}
                placeholder="https://services1.arcgis.com/.../FeatureServer/0"
                onChange={(e) => paste(i, e.target.value)} />
            </Labelled>
            <Labelled label="One line about it">
              <input style={input} value={src.description ?? ""}
                onChange={(e) => set(i, { description: e.target.value })} />
            </Labelled>
            <Labelled label="Photographs layer, if there is one"
              hint="A second layer of site photographs. They are filed against this layer by the code in each photograph's name, worked out from the data rather than set here.">
              <input style={input} value={src.photoUrl ?? ""}
                placeholder="https://services1.arcgis.com/.../Risk_Photos/FeatureServer/0"
                onChange={(e) => set(i, { photoUrl: e.target.value })} />
            </Labelled>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: layer ? T.soft : T.risk }}>
                {src.url
                  ? (layer ? `Reads ${layer}` : "That does not look like a feature layer address.")
                  : "No address yet."}
              </span>
              <Btn small kind="danger"
                onClick={() => onChange(sources.filter((_, n) => n !== i))}>Remove</Btn>
            </div>
          </div>
        );
      })}
      <div>
        <Btn small kind="ghost" onClick={add}>Add a layer</Btn>
      </div>
      <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.6 }}>
        Only layers shared publicly can be read. One that needs a sign in will
        say so plainly rather than showing an empty map. Nothing is copied into
        this app: every screen reads the layer as it stands at that moment.
      </div>
    </div>
  );
}

/* Everything about a contract that decides how its screens read.
   None of it is inherited from another contract, which is the point. */
export default function ContractSettings({ contract, onClose, onSaved }) {
  const [draft, setDraft] = useState({
    workstream_label: contract.workstream_label || "Workstream",
    region_label: contract.region_label || "",
    site_label: contract.site_label || "",
    high_risk_threshold: contract.high_risk_threshold ?? 15,
    schedule_source: contract.schedule_source || "",
  });
  const [workstreams, setWorkstreams] = useState((contract.workstream_list ?? []).join("\n"));
  const [stages, setStages] = useState((contract.stage_list ?? []).join("\n"));
  const [sources, setSources] = useState(
    Array.isArray(contract.map_sources) ? contract.map_sources : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const asList = (text) => text.split("\n").map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true);
    setError("");
    const { error } = await supabase.from("contracts").update({
      workstream_label: draft.workstream_label.trim() || "Workstream",
      region_label: draft.region_label.trim() || null,
      site_label: draft.site_label.trim() || null,
      high_risk_threshold: Number(draft.high_risk_threshold) || 15,
      schedule_source: draft.schedule_source || null,
      workstream_list: asList(workstreams),
      stage_list: asList(stages),
      map_sources: sources
        .filter((s) => (s.title ?? "").trim() && layerUrlOf(s.url))
        .map((s) => ({
          ...s,
          id: s.id, title: s.title.trim(), url: layerUrlOf(s.url),
          where: (s.where ?? "").trim(), description: (s.description ?? "").trim(),
          photoUrl: layerUrlOf(s.photoUrl ?? "") ?? null,
        })),
    }).eq("id", contract.id);
    setBusy(false);
    if (error) { setError(friendlyError(error)); return; }
    await onSaved();
    onClose();
  };

  const term = draft.workstream_label.trim() || "Workstream";

  return (
    <Modal title={`Settings for ${contract.name}`} onClose={onClose} wide>
      <div style={{ display: "grid", gap: 14 }}>
        <Notice>
          These belong to this contract alone. Changing them here changes nothing anywhere else.
        </Notice>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <Labelled label="Word for areas of work"
            hint="WAPA says Workstream. A contract organised by phase says Stage.">
            <input style={input} value={draft.workstream_label}
              onChange={(e) => setDraft({ ...draft, workstream_label: e.target.value })} />
          </Labelled>
          <Labelled label="Word for the wider place" hint="Island for WAPA, District for VIDE. Leave blank if unused.">
            <input style={input} value={draft.region_label} placeholder="Island, District"
              onChange={(e) => setDraft({ ...draft, region_label: e.target.value })} />
          </Labelled>
          <Labelled label="Word for the specific place" hint="Site for WAPA, School for VIDE. Leave blank if unused.">
            <input style={input} value={draft.site_label} placeholder="Site, School"
              onChange={(e) => setDraft({ ...draft, site_label: e.target.value })} />
          </Labelled>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <Labelled label="High risk at a score of"
            hint="15 suits a 5x5 register. A register scoring 1 to 3 tops out at 9 or 12, so 15 would flag nothing.">
            <input style={input} type="number" min={1} value={draft.high_risk_threshold}
              onChange={(e) => setDraft({ ...draft, high_risk_threshold: e.target.value })} />
          </Labelled>
          <Labelled label="Schedule comes from">
            <select style={input} value={draft.schedule_source}
              onChange={(e) => setDraft({ ...draft, schedule_source: e.target.value })}>
              <option value="">Not set</option>
              <option value="msproject">Microsoft Project</option>
              <option value="p6">Primavera P6</option>
              <option value="manual">Kept by hand</option>
            </select>
          </Labelled>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          <Labelled label={`${term} list, one per line`}
            hint={`Leave empty and the ${term.toLowerCase()} field disappears from this contract's forms.`}>
            <textarea style={{ ...input, minHeight: 150, fontFamily: "inherit" }}
              value={workstreams} onChange={(e) => setWorkstreams(e.target.value)} />
          </Labelled>
          <Labelled label="Stage list, one per line"
            hint="The lifecycle stages deliverables sit in. Empty means no stage field.">
            <textarea style={{ ...input, minHeight: 150, fontFamily: "inherit" }}
              value={stages} onChange={(e) => setStages(e.target.value)} />
          </Labelled>
        </div>

        <div>
          <Btn small kind="ghost" onClick={() => setWorkstreams([
            "Project Management", "Systems Integration", "Network Design and Deployment",
            "Meter Design and Deployment", "Organizational Readiness",
            "Public Communications", "Testing", "Training", "Other",
          ].join("\n"))}>Use the standard nine workstreams</Btn>{" "}
          <Btn small kind="ghost" onClick={() => setStages([
            "Preparation and Planning Stage", "Analysis Stage", "Design & Build Stage",
            "Testing Stage", "Training Stage", "Installation Stage",
            "Operations and Stabilization", "Other",
          ].join("\n"))}>Use the AMI lifecycle stages</Btn>
        </div>

        <Labelled label="Live map layers"
          hint="Read straight from ArcGIS and shown under Trackers. Never copied, and edited only where they live.">
          <MapSources sources={sources} onChange={setSources} />
        </Labelled>

        {error && <Notice kind="error">{error}</Notice>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={busy}>{busy ? "Saving..." : "Save settings"}</Btn>
        </div>
      </div>
    </Modal>
  );
}
